import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import {
  MAX_PSYCHIATRIC_CARE_SOLUTIONS,
  SOLUTION_AMBULATORY_ASSISTED_COMMUNITY,
  SOLUTION_AMBULATORY_ASSISTED_LIVING,
  SOLUTION_AMBULATORY_PSYCHIATRIC_CARE,
  SOLUTION_AMBULATORY_PSYCHIATRIC_REHAB,
  SOLUTION_DAY_CARE_CENTRE,
  SOLUTION_GERONTOPSYCHIATRIC_DAY_CLINIC,
  SOLUTION_INTENSIVE_PSYCHIATRIC_CARE_HOME,
  SOLUTION_LONGTERM_PSYCHIATRIC_CARE,
  SOLUTION_MEDICAL_OCCUPATIONAL_REHAB,
  SOLUTION_PSYCHIATRIC_DAY_CLINIC,
  SOLUTION_SHORT_TERM_CARE_PSYCHIATRIC,
  SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_ADDICTIONS,
  SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_INTELLECTUAL,
  SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_PSYCHIATRIC,
  SOLUTION_STATIONARY_PSYCHIATRIC_REHAB,
  SOLUTION_WFBM,
} from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { labelWithOptionalRequiredIcon } from "ds_legacy/components/RequiredIcon";
import { margin } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  Subheading,
} from "ds_legacy/materials/typography";
import { FormWatcher, StateDispatcher } from "react-forms-state";
import { useTranslations } from "translations";

function convertIn(
  inValue: number[] = [],
  props: { items: { id: number }[] },
): Array<{ id: string; label: string; value: boolean }> {
  return props.items.map((v) => ({
    id: v.id.toString(),
    label: "",
    value: (Array.isArray(inValue) ? inValue : []).includes(v.id),
  }));
}

function convertOut(outValue: AnyObject) {
  return outValue
    .filter((v: ToType) => v.value)
    .map((v: ToType) => (isNaN(v.id) ? v.id : parseInt(v.id)));
}

type Section = {
  marginTop?: number;
  solutionIds: number[];
  titleKey: "ambulatoryCare" | "hospitalizationDayFull" | "psychiatricRehab";
};

function PsychiatricCareSolutionsSelectInner({
  items,
}: {
  items: Array<{ id: number; label: string }>;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const sections: Section[] = [
    {
      marginTop: 0,
      solutionIds: [
        SOLUTION_AMBULATORY_ASSISTED_LIVING,
        SOLUTION_AMBULATORY_ASSISTED_COMMUNITY,
        SOLUTION_AMBULATORY_PSYCHIATRIC_CARE,
        SOLUTION_INTENSIVE_PSYCHIATRIC_CARE_HOME,
      ],
      titleKey: "ambulatoryCare",
    },
    {
      marginTop: 1.5,
      solutionIds: [
        SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_INTELLECTUAL,
        SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_PSYCHIATRIC,
        SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_ADDICTIONS,
        SOLUTION_GERONTOPSYCHIATRIC_DAY_CLINIC,
        SOLUTION_SHORT_TERM_CARE_PSYCHIATRIC,
        SOLUTION_LONGTERM_PSYCHIATRIC_CARE,
        SOLUTION_PSYCHIATRIC_DAY_CLINIC,
        SOLUTION_DAY_CARE_CENTRE,
        SOLUTION_WFBM,
      ],
      titleKey: "hospitalizationDayFull",
    },
    {
      marginTop: 1.5,
      solutionIds: [
        SOLUTION_AMBULATORY_PSYCHIATRIC_REHAB,
        SOLUTION_STATIONARY_PSYCHIATRIC_REHAB,
        SOLUTION_MEDICAL_OCCUPATIONAL_REHAB,
      ],
      titleKey: "psychiatricRehab",
    },
  ];

  const getSectionSolutions = (solutionIds: number[]) =>
    items
      .filter((item) => solutionIds.includes(item.id))
      .map((item) => item.id);

  const itemsWithLabels = items.map((item) => ({
    id: item.id,
    label: item.label || getOntology({ key: item.id, type: "solution" }),
  }));

  return (
    <FormWatcher watchPath="solutions">
      {({ watchedValue }) => {
        const selectedSolutions: number[] = Array.isArray(watchedValue)
          ? watchedValue
          : [];
        const atMax =
          selectedSolutions.length >= MAX_PSYCHIATRIC_CARE_SOLUTIONS;

        return (
          <FormControl component="fieldset" style={{ margin: margin(0) }}>
            <Subheading bold margin={margin(0, 0, 1, 0)}>
              {labelWithOptionalRequiredIcon({
                isRequired: true,
                label: translations.patient.solutionLabel,
              })}
            </Subheading>
            <Body margin={margin(0, 0, 1, 0)}>
              {translations.assessments.formValidation.typeOfCareLimit}
            </Body>

            <div className="flex flex-col" style={{ margin: margin(0) }}>
              {sections.map((section) => {
                const sectionSolutions = getSectionSolutions(
                  section.solutionIds,
                );
                if (sectionSolutions.length === 0) return null;

                return (
                  <div key={section.titleKey}>
                    <Subheading
                      margin={margin(section.marginTop ?? 0, 0, 0.5, 0)}
                      style={{ fontSize: FONT_SIZE_14 }}
                    >
                      {
                        translations.ontologies.solution.values[
                          section.titleKey
                        ]
                      }
                    </Subheading>
                    <FormGroup sx={{ flexDirection: "column" }}>
                      {sectionSolutions.map((solutionId) => {
                        const item = itemsWithLabels.find(
                          (i) => i.id === solutionId,
                        );
                        const isSelected =
                          selectedSolutions.includes(solutionId);
                        return (
                          <CheckboxInputField
                            key={solutionId}
                            disabled={atMax && !isSelected}
                            elementName={solutionId.toString()}
                            isGroup
                            label={item?.label ?? ""}
                          />
                        );
                      })}
                    </FormGroup>
                  </div>
                );
              })}
            </div>
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}

const ConnectedPsychiatricCareSolutionsSelect = StateDispatcher(
  convertIn,
  convertOut,
)<{
  elementName?: string;
  items: Array<{ id: number; label: string }>;
  required?: boolean;
  sideMutation?: (
    value: number[],
    mutateElement: (v: unknown, name: string) => void,
  ) => void;
}>(PsychiatricCareSolutionsSelectInner);

export function PsychiatricCareSolutionsSelect() {
  const allSolutions = [
    SOLUTION_AMBULATORY_ASSISTED_LIVING,
    SOLUTION_AMBULATORY_ASSISTED_COMMUNITY,
    SOLUTION_AMBULATORY_PSYCHIATRIC_CARE,
    SOLUTION_INTENSIVE_PSYCHIATRIC_CARE_HOME,
    SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_INTELLECTUAL,
    SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_PSYCHIATRIC,
    SOLUTION_SPECIAL_LIVING_ARRANGEMENTS_ADDICTIONS,
    SOLUTION_GERONTOPSYCHIATRIC_DAY_CLINIC,
    SOLUTION_SHORT_TERM_CARE_PSYCHIATRIC,
    SOLUTION_LONGTERM_PSYCHIATRIC_CARE,
    SOLUTION_PSYCHIATRIC_DAY_CLINIC,
    SOLUTION_DAY_CARE_CENTRE,
    SOLUTION_WFBM,
    SOLUTION_AMBULATORY_PSYCHIATRIC_REHAB,
    SOLUTION_STATIONARY_PSYCHIATRIC_REHAB,
    SOLUTION_MEDICAL_OCCUPATIONAL_REHAB,
  ];

  return (
    <ConnectedPsychiatricCareSolutionsSelect
      elementName="solutions"
      items={allSolutions.map((id) => ({ id, label: "" }))}
      required
      sideMutation={(
        value: number[],
        mutateElement: (v: unknown, name: string) => void,
      ) => {
        mutateElement(null, "assessment_variant");
      }}
    />
  );
}
